import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { HelpCircle, X, ChevronRight, Smartphone } from "lucide-react";

/**
 * Bottone "Attiva GPS" + dialog con guide per attivare la geolocalizzazione
 * sui principali dispositivi mobili. Cliccabile più volte per ri-richiedere
 * il permesso (utile se l'utente prima ha rifiutato).
 *
 * Props:
 *   onRequest: () => void        // chiamata quando l'utente clicca "Attiva GPS"
 *   busy?: boolean               // disabilita il bottone mentre il GPS si sta attivando
 *   variant?: 'primary'|'inline' // stile (primary = banner grande, inline = compatto)
 *   testIdPrefix?: string        // prefisso data-testid (default 'gps-activate')
 */
export default function GpsActivateButton({ onRequest, busy = false, variant = "primary", testIdPrefix = "gps-activate" }) {
  const [helpOpen, setHelpOpen] = useState(false);

  const handleClick = async () => {
    if (typeof onRequest === "function") onRequest();
  };

  const isPrimary = variant === "primary";

  return (
    <>
      <div
        className={
          isPrimary
            ? "rounded-2xl bg-gradient-to-br from-compass/15 to-gold-light border-2 border-compass/40 p-4 space-y-3"
            : "flex items-center gap-2"
        }
        data-testid={`${testIdPrefix}-wrapper`}
      >
        {isPrimary && (
          <div className="flex items-start gap-2">
            <Smartphone size={20} className="text-compass shrink-0 mt-0.5" />
            <div className="text-sm text-ink-800">
              <div className="font-display font-extrabold">GPS non attivo</div>
              <div className="text-xs text-ink-700">Per giocare devi consentire l'accesso alla posizione del tuo dispositivo.</div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleClick}
            disabled={busy}
            className="btn-tactile !py-2.5 !px-4 flex items-center gap-2 disabled:opacity-50"
            data-testid={`${testIdPrefix}-btn`}
          >
            <Smartphone size={16} /> {busy ? "Attivazione…" : "Attiva GPS"}
          </button>
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            className="!py-2.5 !px-3 rounded-2xl bg-white border-2 border-outline text-ink-800 font-bold text-sm hover:bg-gold-light flex items-center gap-1"
            data-testid={`${testIdPrefix}-help-btn`}
            title="Apri guida per attivare il GPS"
          >
            <HelpCircle size={16} /> Guida
          </button>
        </div>
      </div>

      <GpsHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

// --- Guida dispositivi mobili (iOS + Android) ---
function GpsHelpDialog({ open, onClose }) {
  const [tab, setTab] = useState("ios-safari");

  const TABS = [
    { id: "ios-safari", label: "iPhone · Safari" },
    { id: "ios-chrome", label: "iPhone · Chrome" },
    { id: "android-chrome", label: "Android · Chrome" },
    { id: "android-samsung", label: "Android · Samsung" },
    { id: "android-firefox", label: "Android · Firefox" },
  ];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-3xl bg-parchment border-outline max-w-lg max-h-[85vh] overflow-y-auto" data-testid="gps-help-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-ink-900 flex items-center gap-2">
            <Smartphone className="text-compass" size={22} /> Come attivare il GPS
          </DialogTitle>
          <DialogDescription className="text-ink-700">
            Seleziona il tuo dispositivo per vedere i passaggi precisi. Dopo averli completati, torna qui e clicca <strong>"Attiva GPS"</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="gps-help-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                tab === t.id ? "bg-gold text-ink-900" : "bg-white border border-outline text-ink-700 hover:bg-gold-light"
              }`}
              data-testid={`gps-help-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-3 text-sm text-ink-800" data-testid={`gps-help-content-${tab}`}>
          {tab === "ios-safari" && <IosSafariGuide />}
          {tab === "ios-chrome" && <IosChromeGuide />}
          {tab === "android-chrome" && <AndroidChromeGuide />}
          {tab === "android-samsung" && <AndroidSamsungGuide />}
          {tab === "android-firefox" && <AndroidFirefoxGuide />}
        </div>

        <button
          onClick={onClose}
          className="btn-tactile w-full mt-4 flex items-center justify-center gap-2"
          data-testid="gps-help-close-btn"
        >
          <X size={16} /> Ho capito, chiudi la guida
        </button>
      </DialogContent>
    </Dialog>
  );
}

// --- Step list helper ---
function Steps({ items }) {
  return (
    <ol className="space-y-2">
      {items.map((it, i) => (
        <li key={`step-${i}`} className="flex gap-3 items-start">
          <span className="shrink-0 w-7 h-7 rounded-full bg-gold text-ink-900 font-display font-extrabold text-sm flex items-center justify-center">
            {i + 1}
          </span>
          <span className="flex-1 leading-snug pt-1">{it}</span>
        </li>
      ))}
    </ol>
  );
}

function Note({ children }) {
  return (
    <div className="rounded-2xl bg-gold-light border border-gold/40 p-3 text-xs text-ink-800">
      <strong>💡 Suggerimento:</strong> {children}
    </div>
  );
}

// ---- Singole guide ----
function IosSafariGuide() {
  return (
    <>
      <Steps items={[
        <>Apri <strong>Impostazioni</strong> sul tuo iPhone.</>,
        <>Tocca <strong>Privacy e sicurezza</strong> → <strong>Localizzazione</strong>.</>,
        <>Verifica che <strong>Localizzazione</strong> sia su <strong>ON</strong> (interruttore verde).</>,
        <>Torna indietro e scegli <strong>Safari Siti web</strong> dalla lista.</>,
        <>Imposta su <strong>"Chiedi"</strong> o <strong>"Mentre usi l'app"</strong>.</>,
        <>Torna su Odismap nel browser, clicca <strong>"Attiva GPS"</strong> e premi <strong>Consenti</strong> al popup di Safari.</>,
      ]} />
      <Note>Se non vedi il popup, prova a chiudere e riaprire la scheda Safari. Su iOS il permesso è per scheda.</Note>
    </>
  );
}

function IosChromeGuide() {
  return (
    <>
      <Steps items={[
        <>Apri <strong>Impostazioni</strong> sul tuo iPhone.</>,
        <>Tocca <strong>Chrome</strong> nella lista delle app.</>,
        <>Tocca <strong>Posizione</strong> → seleziona <strong>"Mentre usi l'app"</strong>.</>,
        <>Apri Chrome e visita di nuovo Odismap.</>,
        <>Tocca <strong>"Attiva GPS"</strong> e premi <strong>Consenti</strong> al popup.</>,
      ]} />
      <Note>Chrome su iOS usa lo stesso motore di Safari (WebKit), quindi anche il permesso di iOS deve essere abilitato su <em>Impostazioni → Privacy → Localizzazione</em>.</Note>
    </>
  );
}

function AndroidChromeGuide() {
  return (
    <>
      <Steps items={[
        <>Apri le <strong>Impostazioni</strong> del telefono → <strong>Posizione</strong> e verifica che sia <strong>attiva</strong>.</>,
        <>Apri <strong>Chrome</strong>, tocca i 3 puntini in alto a destra → <strong>Impostazioni</strong>.</>,
        <>Vai su <strong>Impostazioni sito</strong> → <strong>Posizione</strong>.</>,
        <>Cerca <strong>quest-maps-2.preview.emergentagent.com</strong> (o <em>quest-maps-2.emergent.host</em>) e impostalo su <strong>"Consenti"</strong>. Se è bloccato, toccalo e seleziona <strong>"Cancella e reimposta"</strong>.</>,
        <>Torna su Odismap e tocca <strong>"Attiva GPS"</strong>.</>,
      ]} />
      <Note>Se hai accidentalmente premuto "Blocca", devi sbloccarlo dalle impostazioni del sito: il popup non riapparirà più finché non lo resetti.</Note>
    </>
  );
}

function AndroidSamsungGuide() {
  return (
    <>
      <Steps items={[
        <>Apri <strong>Impostazioni</strong> → <strong>Posizione</strong> e attivala.</>,
        <>Apri <strong>Internet</strong> (il browser Samsung), tocca le 3 linee in basso a destra → <strong>Impostazioni</strong>.</>,
        <>Vai su <strong>Siti web e download</strong> → <strong>Autorizzazioni siti web</strong> → <strong>Posizione</strong>.</>,
        <>Trova Odismap nella lista, toccalo e imposta su <strong>"Consenti"</strong>.</>,
        <>Torna su Odismap e tocca <strong>"Attiva GPS"</strong>.</>,
      ]} />
      <Note>Su alcuni dispositivi Samsung, l'opzione "Posizione precisa" va abilitata separatamente in Impostazioni → Posizione → Migliora precisione.</Note>
    </>
  );
}

function AndroidFirefoxGuide() {
  return (
    <>
      <Steps items={[
        <>Apri <strong>Impostazioni</strong> Android → <strong>Posizione</strong> e attivala.</>,
        <>Apri <strong>Firefox</strong>, tocca i 3 puntini in basso a destra → <strong>Impostazioni</strong>.</>,
        <>Tocca <strong>Autorizzazioni siti</strong> → <strong>Posizione</strong>.</>,
        <>Impostala su <strong>"Chiedi prima di consentire"</strong> (consigliato) oppure <strong>"Consentito"</strong>.</>,
        <>Torna su Odismap e tocca <strong>"Attiva GPS"</strong>, poi <strong>Consenti</strong> al popup.</>,
      ]} />
      <Note>Se hai un'eccezione bloccata, scorri in fondo a "Posizione" e tocca <em>"Cancella permessi sito"</em>.</Note>
    </>
  );
}
